import { useEffect, useMemo, useState } from "preact/hooks"
import { Loader2, Search } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog"
import { Input } from "../../../../components/ui/input"
import { exactCurrencyFormatter } from "../../../../utils"
import { CalendarInfo, Promotion } from "../../promo_creation/types"
import { CopyForwardOptions } from "../../promo_creation/hooks/use_promotional_context_api"

interface CopyForwardPromotionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  calendars: CalendarInfo[]
  onCopyForward: (options: CopyForwardOptions) => Promise<Promotion[] | null>
  /** Called with the created promotions after a successful copy (e.g. to open the first one). */
  onCopied?: (promotions: Promotion[]) => void
  /** Promotions load lazily per calendar; the dialog needs every calendar hydrated to search across years. */
  onEnsureAllPromotionsLoaded?: () => void
  /** True while calendars/promotions are still hydrating, so an empty list reads as loading, not "none". */
  promotionsLoading?: boolean
}

interface SearchablePromotion extends Promotion {
  calendar_id: string
  calendar_name: string
}

function promotionSearchText(promotion: SearchablePromotion): string {
  return [
    promotion.name,
    promotion.remote_promo_id,
    promotion.customer,
    promotion.retailer,
    promotion.planning_group,
    promotion.account_group,
    promotion.promotion_type,
    promotion.calendar_name,
    promotion.products.join(" "),
    promotion.start_date,
    promotion.end_date,
  ].filter(Boolean).join(" ").toLowerCase()
}

function matchesPromotionSearch(promotion: SearchablePromotion, searchValue: string): boolean {
  const tokens = searchValue.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return true
  const haystack = promotionSearchText(promotion)
  return tokens.every(token => haystack.includes(token))
}

/**
 * Mirrors the server's source-year filter in copy_forward_promotions: a promotion belongs to a
 * source year when its fiscal year, start date, or end date falls in that year.
 */
export function promotionMatchesSourceYear(
  promotion: Pick<Promotion, "fiscal_year" | "start_date" | "end_date">,
  sourceYear: number,
): boolean {
  if (!Number.isFinite(sourceYear)) return true
  if (promotion.fiscal_year === sourceYear) return true
  const years = [promotion.start_date, promotion.end_date]
    .filter((date): date is string => Boolean(date))
    .map(date => Number(date.slice(0, 4)))
  return years.includes(sourceYear)
}

/** Request body for the copy-forward endpoint. Files are copied only when explicitly requested. */
export function buildCopyForwardOptions(input: {
  sourceYear: number
  targetYear: number
  selectedPromotionIds: string[]
  copyFiles: boolean
}): CopyForwardOptions {
  return {
    source_year: input.sourceYear,
    target_year: input.targetYear,
    promotion_ids: input.selectedPromotionIds.length > 0 ? input.selectedPromotionIds : undefined,
    copy_files: input.copyFiles,
  }
}

function formatPromotionSubtitle(promotion: SearchablePromotion): string {
  const parts = [
    promotion.customer,
    promotion.retailer,
    promotion.promotion_type,
    promotion.start_date && promotion.end_date ? `${promotion.start_date} to ${promotion.end_date}` : promotion.start_date || promotion.end_date,
  ].filter(Boolean)
  return parts.join(" • ")
}

export function CopyForwardPromotionDialog({
  open,
  onOpenChange,
  calendars,
  onCopyForward,
  onCopied,
  onEnsureAllPromotionsLoaded,
  promotionsLoading = false,
}: CopyForwardPromotionDialogProps) {
  const [searchValue, setSearchValue] = useState("")
  const [selectedPromotionIds, setSelectedPromotionIds] = useState<string[]>([])
  const [sourceYear, setSourceYear] = useState(new Date().getFullYear())
  const [targetYear, setTargetYear] = useState(new Date().getFullYear() + 1)
  const [copyFiles, setCopyFiles] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Promotions hydrate lazily per calendar/year; make sure every calendar is loaded so the
  // source-year list is complete (previously e.g. current-year promotions could be missing).
  useEffect(() => {
    if (open) onEnsureAllPromotionsLoaded?.()
  }, [open])

  const promotions = useMemo<SearchablePromotion[]>(() => {
    return calendars.flatMap(calendar =>
      calendar.promotions.map(promotion => ({
        ...promotion,
        calendar_id: calendar.id,
        calendar_name: calendar.calendar_name,
      }))
    )
  }, [calendars])

  const filteredPromotions = useMemo(() => {
    return promotions.filter(promotion =>
      promotionMatchesSourceYear(promotion, sourceYear) && matchesPromotionSearch(promotion, searchValue)
    )
  }, [promotions, searchValue, sourceYear])

  function togglePromotion(promotionId: string) {
    setSelectedPromotionIds(previousIds =>
      previousIds.includes(promotionId)
        ? previousIds.filter(id => id !== promotionId)
        : [...previousIds, promotionId]
    )
  }

  async function handleCopyForward() {
    setIsSaving(true)
    try {
      const copied = await onCopyForward(
        buildCopyForwardOptions({ sourceYear, targetYear, selectedPromotionIds, copyFiles })
      )
      if (copied) {
        setSelectedPromotionIds([])
        setSearchValue("")
        onOpenChange(false)
        if (copied.length > 0) onCopied?.(copied)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Copy Promotions Forward</DialogTitle>
          <DialogDescription>
            Search promotions like deduction matching, select the promotions to copy, then choose the target year.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-[1fr_130px_130px_auto]">
            <div class="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                className="pl-9"
                value={searchValue}
                onInput={(event: Event) => setSearchValue((event.target as HTMLInputElement).value)}
                placeholder="Search by promotion, customer, retailer, type, product, ID, or calendar"
              />
            </div>
            <label class="space-y-1 text-xs font-medium text-gray-600">
              Source Year
              <Input type="number" value={sourceYear} onInput={(event: Event) => setSourceYear(Number((event.target as HTMLInputElement).value))} aria-label="Source year" />
            </label>
            <label class="space-y-1 text-xs font-medium text-gray-600">
              Target Year
              <Input type="number" value={targetYear} onInput={(event: Event) => setTargetYear(Number((event.target as HTMLInputElement).value))} aria-label="Target year" />
            </label>
            <label class="mt-5 flex items-center gap-2 whitespace-nowrap text-sm text-gray-600">
              <input type="checkbox" checked={copyFiles} onChange={(event) => setCopyFiles((event.target as HTMLInputElement).checked)} />
              Copy files
            </label>
          </div>

          <div class="max-h-[52vh] overflow-auto rounded-lg border border-gray-200">
            {filteredPromotions.length === 0 && promotionsLoading ? (
              <div class="flex items-center justify-center gap-2 p-6 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading promotions...
              </div>
            ) : filteredPromotions.length === 0 ? (
              <p class="p-6 text-center text-sm text-gray-500">
                No promotions found for source year {sourceYear}
                {searchValue ? " and this search" : ""}. Try a different source year.
              </p>
            ) : (
              <div class="divide-y divide-gray-100">
                {filteredPromotions.map(promotion => {
                  const promotionId = promotion.id || ""
                  const isSelected = selectedPromotionIds.includes(promotionId)
                  return (
                    <button
                      key={promotionId || `${promotion.calendar_id}-${promotion.name}`}
                      type="button"
                      class={`flex w-full items-start gap-3 p-3 text-left transition-colors ${isSelected ? "bg-blue-50" : "bg-white hover:bg-gray-50"}`}
                      onClick={() => promotionId && togglePromotion(promotionId)}
                      disabled={!promotionId}
                    >
                      <input class="mt-1" type="checkbox" checked={isSelected} readOnly />
                      <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="font-medium text-gray-900">{promotion.name}</span>
                          {promotion.remote_promo_id ? <span class="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{promotion.remote_promo_id}</span> : null}
                          {promotion.stage ? <span class="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">{promotion.stage}</span> : null}
                        </div>
                        <p class="mt-1 text-xs text-gray-500">{formatPromotionSubtitle(promotion)}</p>
                        <p class="mt-1 text-xs text-gray-400">{promotion.calendar_name}</p>
                      </div>
                      {promotion.planned_amount ? (
                        <span class="text-sm font-medium text-gray-700">{exactCurrencyFormatter(promotion.planned_amount)}</span>
                      ) : null}
                    </button>
                  )
                })}
                {promotionsLoading && (
                  <div class="flex items-center justify-center gap-2 p-3 text-xs text-gray-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading more promotions...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleCopyForward} disabled={isSaving}>
            {isSaving ? "Copying..." : selectedPromotionIds.length > 0 ? `Copy ${selectedPromotionIds.length} Promotion${selectedPromotionIds.length === 1 ? "" : "s"}` : "Copy All Matching Year"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("promotionMatchesSourceYear", () => {
    it("matches on fiscal year", () => {
      expect(promotionMatchesSourceYear({ fiscal_year: 2026 }, 2026)).toBe(true)
      expect(promotionMatchesSourceYear({ fiscal_year: 2025 }, 2026)).toBe(false)
    })

    it("matches on start or end date year", () => {
      expect(promotionMatchesSourceYear({ start_date: "2026-03-01", end_date: "2026-03-31" }, 2026)).toBe(true)
      expect(promotionMatchesSourceYear({ start_date: "2025-12-01", end_date: "2026-01-15" }, 2026)).toBe(true)
      expect(promotionMatchesSourceYear({ start_date: "2025-12-01", end_date: "2026-01-15" }, 2025)).toBe(true)
      expect(promotionMatchesSourceYear({ start_date: "2025-03-01", end_date: "2025-03-31" }, 2026)).toBe(false)
    })

    it("prefers any matching signal (fiscal year OR dates)", () => {
      expect(promotionMatchesSourceYear({ fiscal_year: 2026, start_date: "2025-11-01" }, 2025)).toBe(true)
      expect(promotionMatchesSourceYear({ fiscal_year: 2026, start_date: "2025-11-01" }, 2026)).toBe(true)
    })

    it("does not match promotions with no year information", () => {
      expect(promotionMatchesSourceYear({}, 2026)).toBe(false)
    })

    it("passes everything when the year input is not a number", () => {
      expect(promotionMatchesSourceYear({}, Number.NaN)).toBe(true)
    })
  })

  describe("buildCopyForwardOptions", () => {
    it("only copies files when explicitly requested (regression: details-only default)", () => {
      const base = { sourceYear: 2026, targetYear: 2027, selectedPromotionIds: [] }
      expect(buildCopyForwardOptions({ ...base, copyFiles: false }).copy_files).toBe(false)
      expect(buildCopyForwardOptions({ ...base, copyFiles: true }).copy_files).toBe(true)
    })

    it("omits promotion_ids when nothing is selected (copy-all-matching-year mode)", () => {
      expect(
        buildCopyForwardOptions({ sourceYear: 2026, targetYear: 2027, selectedPromotionIds: [], copyFiles: false })
          .promotion_ids
      ).toBeUndefined()
      expect(
        buildCopyForwardOptions({ sourceYear: 2026, targetYear: 2027, selectedPromotionIds: ["p1"], copyFiles: false })
          .promotion_ids
      ).toEqual(["p1"])
    })
  })
}

